import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Alert, Spinner, InputGroup, Card } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { backendURL } from './IPaddress';
import './CSS/LoginForm.css';

function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [message, setMessage] = useState(null);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) setMessage('Invalid reset link. Please request a new password reset.');
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setMessage('Passwords do not match.');
            setIsError(true);
            return;
        }
        if (password.length < 8) {
            setMessage('Password must be at least 8 characters.');
            setIsError(true);
            return;
        }
        setIsLoading(true);
        setMessage(null);
        setIsError(false);
        try {
            const response = await fetch(`${backendURL}/api/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    new_password: password,
                    confirm_password: confirmPassword,
                }),
                credentials: 'include',
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setMessage(data.detail || 'Reset failed. The link may have expired.');
                setIsError(true);
                return;
            }
            setSuccess(true);
            setMessage(data.message || 'Password has been reset. You can now log in.');
        } catch (err) {
            setMessage('Unable to reach the server. Please try again later.');
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="auth-page">
                <Container className="auth-page__container">
                    <Row className="justify-content-center">
                        <Col xs={11} sm={10} md={5} lg={4}>
                            <Card className="auth-card shadow-sm">
                                <Card.Body>
                                    <div className="auth-card__header mb-3">
                                        <h1 className="auth-card__title">Invalid link</h1>
                                        <p className="auth-card__subtitle mb-0">This reset link is invalid or has expired.</p>
                                    </div>
                                    <Alert variant="danger" className="mb-3">
                                        Please request a new password reset link.
                                    </Alert>
                                    <div className="auth-card__links">
                                        <Link to="/forgot-password" className="auth-card__link">Request new reset link</Link>
                                        <span className="auth-card__separator" aria-hidden="true">·</span>
                                        <Link to="/login" className="auth-card__link">Back to login</Link>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    if (success) {
        return (
            <div className="auth-page">
                <Container className="auth-page__container">
                    <Row className="justify-content-center">
                        <Col xs={11} sm={10} md={5} lg={4}>
                            <Card className="auth-card shadow-sm">
                                <Card.Body>
                                    <div className="auth-card__header mb-3">
                                        <h1 className="auth-card__title">Password reset</h1>
                                        <p className="auth-card__subtitle mb-0">You can now sign in with your new password.</p>
                                    </div>
                                    <Alert variant="success" className="mb-3">{message}</Alert>
                                    <div className="auth-card__links">
                                        <Link to="/login" className="auth-card__link">Log in</Link>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <Container className="auth-page__container">
                <Row className="justify-content-center">
                    <Col xs={11} sm={10} md={5} lg={4}>
                        <Card className="auth-card shadow-sm">
                            <Card.Body>
                                <div className="auth-card__header mb-4">
                                    <h1 className="auth-card__title">Set new password</h1>
                                    <p className="auth-card__subtitle mb-0">Enter and confirm your new password below.</p>
                                </div>

                                <Form onSubmit={handleSubmit}>
                                    {message && (
                                        <Alert variant={isError ? 'danger' : 'info'} className="mb-3" onClose={() => setMessage(null)} dismissible>
                                            {message}
                                        </Alert>
                                    )}

                                    <Form.Group controlId="password" className="mb-3">
                                        <Form.Label className="auth-card__label">New password</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="At least 8 characters"
                                                required
                                                minLength={8}
                                                autoComplete="new-password"
                                                className="auth-card__input py-2"
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                onClick={() => setShowPassword((s) => !s)}
                                                className="auth-card__password-toggle"
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                aria-pressed={showPassword}
                                            >
                                                <span
                                                    className={`password-visibility-icon ${showPassword ? 'password-visibility-icon--hide' : 'password-visibility-icon--show'}`}
                                                    aria-hidden="true"
                                                />
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>

                                    <Form.Group controlId="confirmPassword" className="mb-3">
                                        <Form.Label className="auth-card__label">Confirm new password</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="Confirm new password"
                                                required
                                                minLength={8}
                                                autoComplete="new-password"
                                                className="auth-card__input py-2"
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                onClick={() => setShowConfirmPassword((s) => !s)}
                                                className="auth-card__password-toggle"
                                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                                aria-pressed={showConfirmPassword}
                                            >
                                                <span
                                                    className={`password-visibility-icon ${showConfirmPassword ? 'password-visibility-icon--hide' : 'password-visibility-icon--show'}`}
                                                    aria-hidden="true"
                                                />
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isLoading}
                                        className="auth-card__submit py-2 mb-3"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                {' '}Saving...
                                            </>
                                        ) : (
                                            'Reset password'
                                        )}
                                    </Button>

                                    <div className="auth-card__links">
                                        <Link to="/login" className="auth-card__link">Back to login</Link>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default ResetPasswordPage;
